// =============================================================================
// kb-client.http.ts — Adaptateur HTTP RÉEL du KbClient (R15-COM-04).
// =============================================================================
// Une SEULE classe paramétrée par le PROFIL (admin / supplier) : le préfixe de base et le cookie CSRF
// double-submit sont figés à la construction (jamais choisis par l'appelant).
//   - ADMIN      → base `/api/communication/admin/kb`, CSRF `admin_csrf` (parité chat/reminders) ;
//   - FOURNISSEUR → base `/api/communication/kb`,       CSRF `supplier_csrf`.
//
// La classe implémente KbAdminClient (surface étendue). Le SEAM supplier l'expose typée `KbClient`
// (LECTURE SEULE) : les capacités admin (tags/export) ne sont JAMAIS invoquées côté fournisseur (§3.9),
// et le serveur reste fail-closed de toute façon (aucune route tag/export sous `/communication/kb`).
//
// SÉCURITÉ : le secret de session (cookie httpOnly) n'est JAMAIS lu ici — porté par `credentials:'include'`.
// Les LECTURES (GET) n'exigent AUCUN CSRF. Les MUTATIONS (POST/DELETE) portent l'en-tête double-submit
// `x-csrf-token` = cookie non-httpOnly du profil ; sans lui, l'API répond 403 (fail-closed serveur). Le
// siret/contactId viennent de la SESSION côté API (jamais du body). MAPPING ERREURS par STATUS HTTP.

import {
  KbApiError,
  type KbAdminClient,
  type KbApiErrorKind,
  type KbExportFormat,
  type KbFilters,
  type KbProfile,
  type KbTagsListDto,
  type KbThreadDetailDto,
  type KbThreadsListDto,
  type CreateTagResultDto,
} from '@/api/kb-client';

/** Forme du corps d'erreur NestJS (HttpException standard) — message éventuellement string[]. */
interface NestErrorBody {
  readonly statusCode?: number;
  readonly message?: string | readonly string[];
  readonly error?: string;
}

/** Mapping STATUS HTTP → kind UX (401 session, 403 garde/CSRF, 404 ressource). */
const STATUS_TO_KIND: Readonly<Record<number, KbApiErrorKind>> = {
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not-found',
};

/** Configuration figée par profil : préfixe de base (sous `/api`) + nom du cookie CSRF double-submit. */
interface KbProfileConfig {
  readonly pathPrefix: string;
  readonly csrfCookieName: string;
}

const PROFILE_CONFIG: Readonly<Record<KbProfile, KbProfileConfig>> = {
  admin: { pathPrefix: 'communication/admin/kb', csrfCookieName: 'admin_csrf' },
  supplier: { pathPrefix: 'communication/kb', csrfCookieName: 'supplier_csrf' },
};

/**
 * Lit la valeur d'un cookie NON-httpOnly (lisible par le SPA même origine). `undefined` si absent — l'appel
 * mutant partira alors sans en-tête et l'API répondra 403 (fail-closed serveur). Parité chat-client.http.
 */
function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === name) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/** Sérialise les filtres en query params (clés absentes/vides omises). `supplierSiret` admin-only. */
function buildQuery(profile: KbProfile, filters: KbFilters): string {
  const params = new URLSearchParams();
  if (filters.q !== undefined && filters.q.length > 0) {
    params.set('q', filters.q);
  }
  if (filters.campaignId !== undefined && filters.campaignId.length > 0) {
    params.set('campaignId', filters.campaignId);
  }
  // `supplierSiret` n'a de sens QUE pour l'admin — jamais émis côté supplier (fail-safe).
  if (
    profile === 'admin' &&
    filters.supplierSiret !== undefined &&
    filters.supplierSiret.length > 0
  ) {
    params.set('supplierSiret', filters.supplierSiret);
  }
  if (filters.archivedFrom !== undefined && filters.archivedFrom.length > 0) {
    params.set('archivedFrom', filters.archivedFrom);
  }
  if (filters.archivedTo !== undefined && filters.archivedTo.length > 0) {
    params.set('archivedTo', filters.archivedTo);
  }
  return params.toString();
}

/**
 * Adaptateur HTTP réel : appels `fetch` contre l'API KB. `baseUrl` par défaut `/api`. Le `profile` FIGE
 * le préfixe et le cookie CSRF. Implémente la surface ADMIN ; le seam supplier l'expose typée `KbClient`.
 */
export class HttpKbClient implements KbAdminClient {
  private readonly config: KbProfileConfig;

  public constructor(
    private readonly profile: KbProfile,
    private readonly baseUrl: string = '/api',
  ) {
    this.config = PROFILE_CONFIG[profile];
  }

  /** Construit l'URL absolue (baseUrl + préfixe de profil + chemin relatif). */
  private url(path: string): string {
    return `${this.baseUrl}/${this.config.pathPrefix}${path}`;
  }

  public listThreads(filters: KbFilters): Promise<KbThreadsListDto> {
    const query = buildQuery(this.profile, filters);
    return this.get<KbThreadsListDto>(
      `/threads${query.length > 0 ? `?${query}` : ''}`,
    );
  }

  public getThreadDetail(threadId: string): Promise<KbThreadDetailDto> {
    return this.get<KbThreadDetailDto>(
      `/threads/${encodeURIComponent(threadId)}`,
    );
  }

  public listTags(): Promise<KbTagsListDto> {
    return this.get<KbTagsListDto>('/tags');
  }

  public createTag(
    label: string,
    parentTagId?: string,
  ): Promise<CreateTagResultDto> {
    const body =
      parentTagId !== undefined && parentTagId.length > 0
        ? { label, parentTagId }
        : { label };
    return this.mutate<CreateTagResultDto>('POST', '/tags', JSON.stringify(body));
  }

  public async deleteTag(tagId: string): Promise<void> {
    await this.mutateNoContent(
      'DELETE',
      `/tags/${encodeURIComponent(tagId)}`,
      undefined,
    );
  }

  public async assignTag(threadId: string, tagId: string): Promise<void> {
    await this.mutateNoContent(
      'POST',
      `/threads/${encodeURIComponent(threadId)}/tags/${encodeURIComponent(tagId)}`,
      undefined,
    );
  }

  public async unassignTag(threadId: string, tagId: string): Promise<void> {
    await this.mutateNoContent(
      'DELETE',
      `/threads/${encodeURIComponent(threadId)}/tags/${encodeURIComponent(tagId)}`,
      undefined,
    );
  }

  public buildExportUrl(format: KbExportFormat, filters: KbFilters): string {
    const query = buildQuery(this.profile, filters);
    return this.url(`/export.${format}${query.length > 0 ? `?${query}` : ''}`);
  }

  /** GET (cookie de session, aucun CSRF) → JSON typé, ou lève une erreur mappée. */
  private async get<T>(path: string): Promise<T> {
    const response = await fetch(this.url(path), {
      method: 'GET',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as T;
  }

  /** MUTATION renvoyant du JSON (POST /tags). En-tête CSRF double-submit du profil. */
  private async mutate<T>(
    method: 'POST' | 'DELETE',
    path: string,
    body: string | undefined,
  ): Promise<T> {
    const response = await this.rawMutate(method, path, body);
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as T;
  }

  /** MUTATION sans corps de réponse (204 : assign/unassign/delete). N'analyse aucun JSON. */
  private async mutateNoContent(
    method: 'POST' | 'DELETE',
    path: string,
    body: string | undefined,
  ): Promise<void> {
    const response = await this.rawMutate(method, path, body);
    if (!response.ok) {
      throw await this.toApiError(response);
    }
  }

  /** Exécute le fetch mutant commun (CSRF double-submit, credentials). */
  private rawMutate(
    method: 'POST' | 'DELETE',
    path: string,
    body: string | undefined,
  ): Promise<Response> {
    const headers: Record<string, string> = {
      'content-type': 'application/json',
    };
    const csrf = readCookie(this.config.csrfCookieName);
    if (csrf !== undefined) {
      headers['x-csrf-token'] = csrf;
    }
    return fetch(this.url(path), {
      method,
      credentials: 'include',
      headers,
      ...(body !== undefined ? { body } : {}),
    });
  }

  /** Convertit une réponse non-OK en erreur applicative typée (message serveur relayé + kind par status). */
  private async toApiError(response: Response): Promise<KbApiError> {
    const body = await this.safeJson(response);
    const message =
      this.extractMessage(body) ?? `Erreur API (${String(response.status)}).`;
    const kind = STATUS_TO_KIND[response.status] ?? 'generic';
    return new KbApiError(message, kind);
  }

  private extractMessage(body: NestErrorBody | undefined): string | undefined {
    const message = body?.message;
    if (message === undefined) {
      return undefined;
    }
    return typeof message === 'string' ? message : message.join(' ');
  }

  private async safeJson(response: Response): Promise<NestErrorBody | undefined> {
    try {
      return (await response.json()) as NestErrorBody;
    } catch {
      return undefined;
    }
  }
}
